import { defineComponent, h } from 'vue';
import BaseComponent from "./baseFormMixin.jsx";
import arrayExpressionMixin from './arrayExpressionField.jsx';
import utils from "../../../Shared/utils.jsx";
import methods from "../../../Shared/methods";
import careHelpfulFunctions from "../../careHelpfulFunctions.jsx";

import { appSettings } from "@/Shared/appSettings.js";
import { WithProxy } from '@/Shared/helperClasses';

/* This component handles three different schema configurations:
 * 1. items with an enum
 * 2. items with a titleMap
 * 3. formatData with LookupURL
 * 
 * All three produce a list of items which will generate a set of checkbox input elements.
 * 
 * Example of a titleMap:

            "Notifications": {
              "$typeSchema": "/schema/public/Platform.Schema.SchemaSchemas.v1/SchemaSchema_Type_Array-CheckBoxes-TitleMap",
              "type": "array",
              "arrayStart": "FormDefault",
              "format": "checkboxes",
              "uniqueItems": false,
              "dynamicColumns": 1,
              "items": {
                "type": "string",
                "titleMap": [
                  {
                    "name": "Main Contact",
                    "value": "Main"
                  },
                  {
                    "name": "Billing",
                    "value": "Billing"
                  },
                  {
                    "name": "Technical Requests/Issuses",
                    "value": "Technical"
                  },
                  {
                    "name": "Platform Maintenance",
                    "value": "Maintenance"
                  },
                  {
                    "name": "Platform Outage",
                    "value": "Outage"
                  }
                ]
              }
            }

 * The output of this form element is a string array. Ex: ["Main","Technical","Maintenance",...]
 */

import TranslationContainer from './../TranslationContainer.jsx';
import { QCheckbox, QIcon, QCard, QCardSection } from 'quasar';

export default defineComponent({
    name: 'sform-checkboxes-array',
    mixins: [BaseComponent, arrayExpressionMixin],
    components: {
        TranslationContainer,
        QCheckbox,
        QIcon,
        QCard,
        QCardSection,
    },
    data: () => ({
        items: null,
        lookupurl: null,
        lookupraw: null,
        displayexpr: null,
        valueexpr: null,
        groupbyexpr: null,
        prependIconExpr: null,
        appendIconsExpr: null,
        isdisabledExpr: null,
        readOnlyEval: null,
    }),
    props: {},
    created() {
        if (
            this.element.items &&
            this.element.items.length > 0 &&
            this.element.items[0].titleMap) {
            this.items = this.element.items[0].titleMap;
        }
        else if (
            this.element.items &&
            this.element.items.length > 0 &&
            this.element.items[0].enum) {
            this.items = this.element.items[0].enum.map((i) => ({ name: i, value: i }));
        }
        else if (this.element.formatData && this.element.formatData.LookupType == 'URL' && this.element.formatData.LookupURL) {
            // Compile url, create functions for mapping item name and item value
            this.lookupurl = utils.compile(this, this.element.formatData.LookupURL);
        }
        else if (this.element.formatData && this.element.formatData.LookupInterpolatedExpression) {
            this.lookupraw = utils.compileExpression(this, this.element.formatData.LookupInterpolatedExpression);
        }

        if (this.element.formatData) {    
            if (this.element.formatData.DisplayType == "Expression" && this.element.formatData.DisplayExpression)
                this.displayexpr = new Function(
                    "context",
                    this.element.formatData.LookupModelAs,
                    "util",
                    "with(context) return " + this.element.formatData.DisplayExpression + ";"
                );

            if (this.element.formatData.ValueType == "Expression" && this.element.formatData.ValueExpression)
                this.valueexpr = new Function(
                    "context",
                    this.element.formatData.LookupModelAs,
                    "util",
                    `with(context) return ${this.element.formatData.ValueExpression};`
                );

            if (this.element.formatData.GroupByType == "Expression" && this.element.formatData.GroupByExpression)
                this.groupbyexpr = new Function(
                    "context",
                    this.element.formatData.LookupModelAs,
                    "util",
                    `with(context) return ${this.element.formatData.GroupByExpression};`
                );

            if (this.element.formatData.PrependIconExpression)
                this.prependIconExpr = new Function(
                    "context",
                    this.element.formatData.LookupModelAs,
                    "util",
                    `with(context) return ${this.element.formatData.PrependIconExpression};`
                );

            if (this.element.formatData.AppendIconsExpression)
                this.appendIconsExpr = new Function(
                    "context",
                    this.element.formatData.LookupModelAs,
                    "util",
                    `with(context) return ${this.element.formatData.AppendIconsExpression};`
                );
            if (this.element.formatData.IsDisabledExpression)
                this.isdisabledExpr = new Function(
                    "context",
                    this.element.formatData.LookupModelAs,
                    "util",
                    `with(context) return ${this.element.formatData.IsDisabledExpression};`
                );
        }

        if (this.lookupurlvalue)
            this.lookupurlvalue_watch$ = this.$watch(
                function () {
                    return this.lookupurlvalue;
                },
                function (newval, oldval) {
                    this.Refresh();
                }
            );
    },
    watch: {
        lookuprawvalue: function () {
            if (this.element.formatData.WatchForChanges) this.Refresh();
        },
    },
    async mounted() {
        await this.Refresh();

        this.loadComplete();
    },
    computed: {
        readOnly() {
            if (this.appearance?.readonly)
                return true;
            try {
                if(this.readOnlyEval === null && this.element.readonly)
                    this.readOnlyEval = utils.compile(this, this.element.readonly);
                if(this.readOnlyEval)
                    return JSON.parse(utils.evaluate(this.readOnlyEval, this));
                return false;
            }
            catch (e) {
                utils.warn('ReadOnly could not evaluate expression: ' + this.element.readonly + '; ' + e);
                return false;
            }
        },
        lookupurlvalue: function() {
            if (this.lookupurl)
                try {
                    return utils.evaluate(this.lookupurl, this, false, null, true);
                }
                catch (e) {
                    utils.warn(`LookupList lookupurl could not be evaluated: ${this.lookupurl.code}`, e);
                    return '';
                }
            else
                return '';
        },
        lookuprawvalue: function() {
            return this.lookupraw ? utils.evaluate(this.lookupraw, this) : [];
        },
        items_translated: function() {
            const selfproxy = new WithProxy(this).proxy;

            if (this.items) {
                const items = this.items.map((item) => {
                    let name;
                    let value;
                    let group;
                    let icon;
                    let icons;
                    let isDisabled;

                    // If using a LookupURL, do the name/value transformations
                    if (
                        this.element.formatData &&
                        (this.element.formatData.LookupURL || this.element.formatData.LookupInterpolatedExpression)
                    ) {
                        name = this.displayexpr
                            ? this.displayexpr(selfproxy, item, careHelpfulFunctions)
                            : item[this.element.formatData.DisplayField];
                        value = this.valueexpr
                            ? this.valueexpr(selfproxy, item, careHelpfulFunctions)
                            : item[this.element.formatData.ValueField];
                        if (this.element.formatData.GroupByType && this.element.formatData.GroupByType != 'None')
                            group = this.groupbyexpr
                                ? this.groupbyexpr(selfproxy, item, careHelpfulFunctions)
                                : item[this.element.formatData.GroupByField];
                    }
                    else {
                        name = item.name;
                        value = item.value;
                        group = '';
                    }

                    if (this.prependIconExpr) {
                        icon = this.prependIconExpr(selfproxy, item, careHelpfulFunctions);
                    }

                    if (this.appendIconsExpr) {
                        icons = this.appendIconsExpr(selfproxy, item, careHelpfulFunctions);
                    }

                    if (this.isdisabledExpr) {
                        isDisabled = this.isdisabledExpr(selfproxy, item, careHelpfulFunctions);
                    }

                    return {
                        name: name,
                        value: value,
                        group: group,
                        icon: icon,
                        icons: icons,
                        isDisabled: isDisabled
                    };
                });

                // Sorting not desirable - disabled in Vue2 - perhaps we need a setting in the form item
                //items.sort((a, b) => {
                //    // First compare by group
                //    if (a.group !== b.group) {
                //        return a.group?.localeCompare(b.group) || 0;
                //    }
                //    // If groups are equal, compare by name
                //    return a.name?.localeCompare(b.name) || 0;
                //});

                return items;
            }
            else return [];
        },
    },
    methods: {
        async Refresh() {
            if (this.lookupurl) this.items = await utils.api.get(this.lookupurlvalue);
            else if (this.lookupraw) this.items = this.lookuprawvalue;
        },
        getItemValue(key) {
            return this.itemvalue ? this.itemvalue.some((v) => v == key) : false;
        },
        setItemValue(key, enabled) {
            let tmp = this.itemvalue;
            if (!tmp) tmp = [];

            // Look for the item in the existing array
            const idx = tmp.findIndex((i) => i == key);
            if (enabled) {
                // If we are supposed to add, only add if it isn't already there
                if (idx < 0) tmp.push(key);
            } else {
                // If we are supposed to remove, only remove if it is there
                if (idx >= 0) tmp.splice(idx, 1);
            }

            // Most often, this isn't necessary since we are modifying the referenced array.
            // But in the case where the original value is null, we have to set it.
            this.sync(tmp);
        },
        isDisabledItem(key) {
            if (key != null && key == 'true')
            {
                return true;
            }
            return false;
        },
    },
    render() {
        const expression = this.renderArrayExpressionStandIn({ class: 'c-formCheckboxes', labelSlot: true });
        if (expression)
            return expression;

        const elements = this.items_translated.map((item) => {
            const labelContent = this.element.formatData?.Translate 
                ? <TranslationContainer context={this} value={item.name} /> 
                : <span>{item.name}</span>;

            const labelSlot = () => (
                <div class="row items-center no-wrap">
                    {labelContent}
                    {item.icons && (
                        <div class="row q-ml-sm no-wrap">
                            {item.icons.map((i, idx) => (
                                <QIcon key={idx} name={i} size="sm" class="q-ml-xs" />
                            ))}
                        </div>
                    )}
                </div>
            );
            
            return {
                group: item.group,
                element: (
                    <div class="row items-center no-wrap" style={{ width: "100%" }}>
                        {item.icon && <QIcon name={item.icon} size="sm" class="q-mr-sm" />}
                        <QCheckbox
                            class="caption q-pa-none q-ma-none flex-grow-1"
                            dense
                            style={{ width: "100%" }}
                            modelValue={this.getItemValue(item.value)}
                            onUpdate:modelValue={(v) => this.setItemValue(item.value, v == true)}
                            onFocus={this.onFocus}
                            onBlur={this.onBlur}
                            disable={this.isDisabledItem(item.isDisabled) || this.readOnly}
                        >
                            {{ default: labelSlot }}
                        </QCheckbox>
                    </div>
                )
            };
        });

        if (this.element.schema.displayFormat == "Grid" && this.element.schema.displayColumns > 0) {
            var rows = [];
            var template = [];

            for (var i = 0; i < this.element.schema.displayColumns; i++) {
                template.push(undefined);
            }

            let last_group = '';
            let c = 0;
            for (let i = 0; i < elements.length; i++) {
                const element = elements[i];
                if (element.group && element.group !== last_group) {
                    // break the group and start a new one
                    c = 0;
                    rows.push(<div class="mt-2">{element.group}</div>);
                }
                last_group = element.group;

                const colIndex = c % this.element.schema.displayColumns;

                if (colIndex == 0) { // new row so make a new row
                    rows.push([...template]);
                }

                rows[rows.length - 1][colIndex] = element.element;
                c++;
            }

            const colSize = 12 / this.element.schema.displayColumns;
            const colClass = `col-${colSize}`;
            var rowMapped = rows.map((rowElements) => {
                if (Array.isArray(rowElements)) {
                    var chks = rowElements.map((chkbx) => {
                        return (<div class={colClass}>{chkbx}</div>);
                    });

                    return (<div class="row no-wrap">{chks}</div>);
                }
                else
                    return <div class="row no-wrap">{rowElements}</div>;
            });

            return (
                <QCard flat bordered class="rounded-borders q-mt-sm q-mb-sm q-pa-md">
                    <QCardSection class="q-pa-none">
                        <TranslationContainer context={this} value={this.Title}/>
                        <br />
                        {rowMapped}
                    </QCardSection>
                </QCard>
            );
        }
        else {
            return (
                <QCard flat bordered class="rounded-borders q-mt-sm q-mb-sm q-pa-md">
                    <QCardSection class="q-pa-none">
                        <TranslationContainer context={this} value={this.Title}/>
                        <br />
                        {elements.map(e => e.element)}
                    </QCardSection>
                </QCard>
            );
        }
    },
});
